import { Badge, Callout, Skeleton, Tooltip } from '@provenance/design';
import { AlertTriangle, ArrowRight, CheckCircle2, Circle } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export interface ReadinessCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string | null;
  remedyPath: string;
}

export interface Readiness {
  ready: boolean;
  checks: ReadinessCheck[];
  /** Where this chamber's exporters go. */
  links: { submitUrl: string; registerUrl: string };
}

/** Scrolls to a section on this page and moves focus there, so keyboard users land with it. */
export function scrollToSection(sectionId: string): boolean {
  const target = document.getElementById(sectionId);
  if (!target) return false;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
  return true;
}

/**
 * The go-live checklist.
 *
 * The same object the server evaluates before it will activate a chamber — not a second
 * opinion rendered in the browser. A chamber that can receive requests but not stamp them
 * produces a queue nobody can clear, so this is the one screen where a green tick has to
 * mean the server agrees.
 *
 * Each failed check carries its own remedy path. A checklist that tells an administrator
 * what is wrong without telling them where to fix it is a to-do list, not a tool.
 */
export function ReadinessChecklist({
  readiness,
  loading,
  /**
   * Which half of the checklist this viewer can actually act on.
   *
   * The two audiences are disjoint: a platform admin owns the chamber record and cannot
   * reach a chamber's own configuration (the API answers 403 — they have no chamber), and
   * a chamber admin is the reverse. Offering a link either way is a 403 dressed up as a
   * call to action, so the other half is labelled with whose job it is.
   */
  audience,
}: {
  readiness: Readiness | undefined;
  loading?: boolean;
  audience: 'platform' | 'chamber';
}) {
  const { pathname } = useLocation();

  if (loading) return <Skeleton className="h-56" />;
  if (!readiness) return null;

  const outstanding = readiness.checks.filter((c) => !c.passed);

  return (
    <div className="flex flex-col gap-3">
      {readiness.ready ? (
        <Callout
          tone="ok"
          icon={<CheckCircle2 className="size-4" />}
          title="Ready to issue real certificates"
        >
          Every condition the platform checks before activation is met. Nothing here expires —
          but deactivating a seal or disabling a document type can put it back.
        </Callout>
      ) : (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={`${outstanding.length} thing${outstanding.length === 1 ? '' : 's'} still to do before going live`}
        >
          Activation is refused until all of these pass. A chamber that can receive requests but
          cannot stamp them just builds a queue nobody can clear.
        </Callout>
      )}

      <dl className="grid gap-1.5 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3 py-2.5 [font-size:12.5px]">
        <ExporterLink label="Exporters upload certificates at" url={readiness.links.submitUrl} live={readiness.ready} />
        <ExporterLink label="New exporters register at" url={readiness.links.registerUrl} live={readiness.ready} />
      </dl>

      <ul className="flex list-none flex-col gap-0 p-0">
        {readiness.checks.map((check) => {
          const platformOnly = check.remedyPath.startsWith('/platform');
          const linkable = platformOnly === (audience === 'platform');
          const ownedBy = platformOnly ? 'Platform team' : 'Chamber admin';
          return (
            <li
              key={check.key}
              className="flex items-start gap-3 border-b border-[var(--pv-line)] py-2.5 last:border-b-0"
            >
              {check.passed ? (
                <CheckCircle2 aria-hidden className="mt-px size-4 shrink-0 text-[var(--pv-ok)]" />
              ) : (
                <Circle aria-hidden className="mt-px size-4 shrink-0 text-[var(--pv-warn)]" />
              )}
              <div className="min-w-0 flex-1">
                <p className={check.passed ? 'text-[var(--pv-muted)]' : 'font-medium'}>
                  {check.label}
                </p>
                {check.detail && (
                  <p className="mt-0.5 [font-size:12.5px] text-[var(--pv-faint)]">{check.detail}</p>
                )}
              </div>
              {!check.passed &&
                (linkable ? (
                  <Link
                    to={check.remedyPath}
                    onClick={(event) => {
                      // A remedy on the page already open (e.g. #branding) is a scroll, not a
                      // navigation — and a repeated click must still scroll, which a router
                      // navigation to the same URL would not.
                      const [path, section] = check.remedyPath.split('#');
                      if (section && path === pathname && scrollToSection(section)) {
                        event.preventDefault();
                      }
                    }}
                    className="inline-flex shrink-0 items-center gap-1 [font-size:12.5px] font-medium text-[var(--pv-brand)] hover:underline"
                  >
                    Fix this <ArrowRight aria-hidden className="size-3.5" />
                  </Link>
                ) : (
                  <Tooltip content="Not something you can change from your account">
                    <span>
                      <Badge tone="neutral" size="sm">{ownedBy}</Badge>
                    </span>
                  </Tooltip>
                ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The address an exporter actually uses. Nobody had to be told it before, and the first
 * person to guess got the slug wrong. Both pages answer only once the chamber is live.
 */
function ExporterLink({ label, url, live }: { label: string; url: string; live: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <dt className="text-[var(--pv-faint)]">{label}</dt>
      <dd className="flex min-w-0 items-center gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="pv-num break-all font-medium text-[var(--pv-brand)] hover:underline">
          {url}
        </a>
        <button
          type="button"
          className="shrink-0 text-[var(--pv-muted)] underline-offset-2 hover:underline"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        {!live && <span className="text-[var(--pv-faint)]">· opens once the chamber is live</span>}
      </dd>
    </div>
  );
}
